import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {
    CHANGE_FAVORITE,
    FINISH_CHANGING_FAVORITES,
    CLEAR_QUERY,
} from '../Redux/Actions/Types/queryToolActionTypes';
import {usePatchQueryParams} from '../../../monitoring/src/Hooks/RouteHooks';
import {useCreateColumn, useFindColumnById} from './QueryToolColumnHooks';
import {useQueryData} from './QueryToolDataHooks';
import {useDefaultErrorHandler} from "./ErrorHooks";
import {service as SavedQueryService} from '../Services/SavedQueryService';
import {ObjectUtils} from '../Utils/ObjectUtils';

export const useChangeFavorite = () => {
    const dispatch = useDispatch();
    const queryData = useQueryData();
    const createColumnObject = useCreateColumn();
    const findColumnById = useFindColumnById();
    const patchQueryParams = usePatchQueryParams();

    const {isChangingFavorites, skipReload, favorite} = useSelector(state => state.queryToolState);

    useEffect(() => {
        if (isChangingFavorites) {
            patchQueryParams({'savedQueryId': favorite?.savedQueryId});
            if (!skipReload) {
                queryData();
            }
            dispatch({type: FINISH_CHANGING_FAVORITES});
        }
    }, [dispatch, favorite, isChangingFavorites, patchQueryParams, queryData, skipReload]);

    return (nextFavorite, definition, shouldSkipReload = false) => {
        dispatch({
            type: CHANGE_FAVORITE,
            favorite: nextFavorite,
            definition,
            skipReload: shouldSkipReload,
            columns: definition?.columns.map(column => createColumnObject({
                ...findColumnById(column.name),
                ...column,
            })) ?? [],
        });
    };
};

export const useQueryFavoritesController = ({definition, savedQueryId}) => {
    const dispatch = useDispatch();
    const defaultErrorHandler = useDefaultErrorHandler();
    const changeFavorite = useChangeFavorite();

    const [savedQueries, setSavedQueries] = useState([]);
    const [currentFavoriteId, setCurrentFavoriteId] = useState('');
    const [favoriteName, setFavoriteName] = useState('');
    const [changedFavoriteId, setChangedFavoriteId] = useState(null);
    const [showFavoriteModal, setShowFavoriteModal] = useState(false);
    const [showNewQuery, setShowNewQuery] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [changed, setChanged] = useState(false);
    const [isChanging, setIsChanging] = useState(false);

    const getFavorites = useCallback(async () => {
        try {
            const {result} = await SavedQueryService.Get();
            setSavedQueries(result);
            setChanged(false);
        } catch (error) {
            defaultErrorHandler(error);
        }
    }, [defaultErrorHandler]);

    const findFavorite = useCallback((id) => {
        return savedQueries.find(x => x.savedQueryId === Number(id));
    }, [savedQueries]);

    const changeFavoriteItems = useCallback((value) => {
        setCurrentFavoriteId(value ?? '');
        setChangedFavoriteId(null);
        setChanged(false);
        setIsChanging(true);
    }, []);

    const loadFavorite = useCallback((value) => {
        const favorite = findFavorite(value);
        const favoriteDefinition = favorite ? favorite.definition : null;

        changeFavorite(favorite, favoriteDefinition);
    }, [changeFavorite, findFavorite]);

    useEffect(() => {
        if (isChanging) {
            loadFavorite(currentFavoriteId);
            setIsChanging(false);
        }
    }, [isChanging, currentFavoriteId, loadFavorite]);

    useEffect(() => {
        const loadData = async () => {
            await getFavorites();
            if (savedQueryId) {
                changeFavoriteItems(savedQueryId);
            }
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const currentFavorite = findFavorite(currentFavoriteId);

        if (!currentFavorite || isChanging || changed || !(definition?.columns?.length)) {
            return;
        }

        const currentDefinition = {
            ...currentFavorite.definition,
            localTimeZone: undefined,
        };
        const originalDefinition = {
            ...definition,
            localTimeZone: undefined,
        };

        currentDefinition.columns = currentDefinition.columns.map(x => {
            const fields = ['guid', 'name', 'title', 'filters', 'aggregate'];
            const result = {};

            fields.forEach(field => {
                if (x[field] !== undefined) result[field] = x[field];
            });

            return result;
        });

        if (!ObjectUtils.Compare(currentDefinition, originalDefinition)) {
            setChanged(true);
        }
    }, [currentFavoriteId, changed, definition, findFavorite, isChanging]);

    const handleFavoriteSelect = useCallback((value) => {
        if (changed) {
            setChangedFavoriteId(value);
        } else {
            changeFavoriteItems(value);
        }
    }, [changeFavoriteItems, changed]);

    const confirmFavoriteChange = useCallback(() => {
        if (!changedFavoriteId) return;
        changeFavoriteItems(changedFavoriteId);
    }, [changeFavoriteItems, changedFavoriteId]);

    const cancelFavoriteChange = useCallback(() => {
        setChangedFavoriteId(null);
    }, []);

    const openFavoriteModal = useCallback(() => {
        setShowFavoriteModal(true);
    }, []);

    const closeFavoriteModal = useCallback(() => {
        setShowFavoriteModal(false);
    }, []);

    const addFavorite = useCallback(async () => {
        try {
            const {result} = await SavedQueryService.CreateSavedQuery({name: favoriteName, definition});
            setCurrentFavoriteId(result.savedQueryId);
            setSavedQueries(prev => [...prev, result]);
            setShowFavoriteModal(false);
            setFavoriteName('');
            changeFavorite(result, result?.definition, true);
        } catch (error) {
            defaultErrorHandler(error);
        }
    }, [changeFavorite, definition, favoriteName, defaultErrorHandler]);

    const updateFavorite = useCallback(async () => {
        try {
            const data = {
                ...findFavorite(currentFavoriteId),
                definition,
            };

            await SavedQueryService.UpdateSavedQuery(data);
            await getFavorites();
        } catch (error) {
            defaultErrorHandler(error);
        }
    }, [currentFavoriteId, definition, findFavorite, getFavorites, defaultErrorHandler]);

    const openNewQueryDialog = useCallback(() => {
        setShowNewQuery(true);
    }, []);

    const closeNewQueryDialog = useCallback(() => {
        setShowNewQuery(false);
    }, []);

    const confirmNewQuery = useCallback(() => {
        setShowNewQuery(false);
        dispatch({type: CLEAR_QUERY});
        changeFavoriteItems('');
    }, [changeFavoriteItems, dispatch]);

    const openDeleteDialog = useCallback(() => {
        setShowDelete(true);
    }, []);

    const closeDeleteDialog = useCallback(() => {
        setShowDelete(false);
    }, []);

    const confirmDeleteFavorite = useCallback(async () => {
        try {
            await SavedQueryService.DeleteSavedQuery(currentFavoriteId);
            setShowDelete(false);
            changeFavoriteItems('');
            await getFavorites();
        } catch (error) {
            setShowDelete(false);
            defaultErrorHandler(error);
        }
    }, [changeFavoriteItems, currentFavoriteId, getFavorites, defaultErrorHandler]);

    const hasUnsavedChanges = useCallback(() => (
        changed || ((definition?.columns?.length ?? 0) > 0 && !currentFavoriteId)
    ), [changed, currentFavoriteId, definition]);

    const isShared = useMemo(() => Boolean(findFavorite(currentFavoriteId)?.isShared), [currentFavoriteId, findFavorite]);

    return {
        savedQueries,
        currentFavoriteId,
        favoriteName,
        setFavoriteName,
        changedFavoriteId,
        changed,
        showFavoriteModal,
        showNewQuery,
        showDelete,
        openFavoriteModal,
        closeFavoriteModal,
        handleFavoriteSelect,
        confirmFavoriteChange,
        cancelFavoriteChange,
        addFavorite,
        updateFavorite,
        openNewQueryDialog,
        confirmNewQuery,
        closeNewQueryDialog,
        openDeleteDialog,
        confirmDeleteFavorite,
        closeDeleteDialog,
        hasUnsavedChanges,
        isShared,
    };
};
